import type {
  HumanDecisionStatus,
  HumanLifecycleState,
  HumanProvenanceFilters,
  HumanRecordKind,
} from "../../api/humanProvenance";

export interface HumanFilterState {
  recordKinds: HumanRecordKind[];
  actorIds: string[];
  decisionStatuses: HumanDecisionStatus[];
  evidenceTypes: string[];
  entryTypes: string[];
  sensitivityTierIds: string[];
  lifecycleStates: HumanLifecycleState[];
}

export const ALL_HUMAN_RECORD_KINDS = [
  "decision",
  "decision_evidence",
  "journal_entry",
] as const satisfies readonly HumanRecordKind[];

export const DEFAULT_HUMAN_FILTERS: HumanFilterState = {
  recordKinds: [...ALL_HUMAN_RECORD_KINDS],
  actorIds: [],
  decisionStatuses: [],
  evidenceTypes: [],
  entryTypes: [],
  sensitivityTierIds: [],
  lifecycleStates: [],
};

const STATUS = new Set<HumanDecisionStatus>([
  "proposed", "ratified", "under_review", "pending_ratification", "withdrawn", "superseded",
]);
const LIFECYCLE = new Set<HumanLifecycleState>([
  "active", "invalidated", "superseded", "redacted", "abandoned", "inapplicable",
]);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TYPE_IDENTIFIER = /^[a-z][a-z0-9_.-]{0,99}$/;

const PARAMS = {
  recordKinds: "dagHumanRecordKind",
  actors: "dagHumanActor",
  statuses: "dagHumanStatus",
  evidenceTypes: "dagHumanEvidenceType",
  entryTypes: "dagHumanEntryType",
  tiers: "dagHumanTier",
  lifecycle: "dagHumanLifecycle",
} as const;

function unique<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

function boundedValues(params: URLSearchParams, key: string): string[] {
  return unique(params.getAll(key).filter((value) => value.length > 0 && [...value].length <= 100)).slice(0, 50);
}

function boundedUuidValues(params: URLSearchParams, key: string): string[] {
  return unique(
    boundedValues(params, key)
      .filter((value) => UUID.test(value))
      .map((value) => value.toLowerCase()),
  ).sort();
}

function boundedTypeValues(params: URLSearchParams, key: string): string[] {
  return unique(
    boundedValues(params, key)
      .map((value) => value.trim())
      .filter((value) => TYPE_IDENTIFIER.test(value)),
  ).sort();
}

export function canonicalHumanRecordKinds(values: readonly string[]): HumanRecordKind[] {
  const selected = new Set(values);
  const canonical = ALL_HUMAN_RECORD_KINDS.filter((kind) => selected.has(kind));
  return canonical.length ? canonical : [...ALL_HUMAN_RECORD_KINDS];
}

export function parseHumanFilterState(search: string): HumanFilterState {
  const params = new URLSearchParams(search);
  return {
    recordKinds: canonicalHumanRecordKinds(boundedValues(params, PARAMS.recordKinds)),
    actorIds: boundedUuidValues(params, PARAMS.actors),
    decisionStatuses: boundedValues(params, PARAMS.statuses)
      .filter((value): value is HumanDecisionStatus => STATUS.has(value as HumanDecisionStatus)),
    evidenceTypes: boundedTypeValues(params, PARAMS.evidenceTypes),
    entryTypes: boundedTypeValues(params, PARAMS.entryTypes),
    sensitivityTierIds: boundedUuidValues(params, PARAMS.tiers),
    lifecycleStates: boundedValues(params, PARAMS.lifecycle)
      .filter((value): value is HumanLifecycleState => LIFECYCLE.has(value as HumanLifecycleState)),
  };
}

export function humanRecordKindsForRequest(state: HumanFilterState): HumanRecordKind[] {
  return canonicalHumanRecordKinds(state.recordKinds);
}

export function humanFiltersForRequest(state: HumanFilterState): HumanProvenanceFilters {
  return {
    actor_ids: state.actorIds,
    decision_statuses: state.decisionStatuses,
    evidence_types: state.evidenceTypes,
    entry_types: state.entryTypes,
    sensitivity_tier_ids: state.sensitivityTierIds,
    lifecycle_states: state.lifecycleStates,
  };
}

export function humanFilterCount(state: HumanFilterState): number {
  return Number(humanRecordKindsForRequest(state).length !== ALL_HUMAN_RECORD_KINDS.length)
    + Number(state.actorIds.length > 0)
    + Number(state.decisionStatuses.length > 0)
    + Number(state.evidenceTypes.length > 0)
    + Number(state.entryTypes.length > 0)
    + Number(state.sensitivityTierIds.length > 0)
    + Number(state.lifecycleStates.length > 0);
}

function replaceMany(params: URLSearchParams, key: string, values: readonly string[]): void {
  params.delete(key);
  values.forEach((value) => params.append(key, value));
}

/** Replace transient facet state while preserving the exact temporal trio and selection URL. */
export function updateHumanFilterUrl(state: HumanFilterState): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const recordKinds = humanRecordKindsForRequest(state);
  replaceMany(
    url.searchParams,
    PARAMS.recordKinds,
    recordKinds.length === ALL_HUMAN_RECORD_KINDS.length ? [] : recordKinds,
  );
  replaceMany(url.searchParams, PARAMS.actors, state.actorIds);
  replaceMany(url.searchParams, PARAMS.statuses, state.decisionStatuses);
  replaceMany(url.searchParams, PARAMS.evidenceTypes, state.evidenceTypes);
  replaceMany(url.searchParams, PARAMS.entryTypes, state.entryTypes);
  replaceMany(url.searchParams, PARAMS.tiers, state.sensitivityTierIds);
  replaceMany(url.searchParams, PARAMS.lifecycle, state.lifecycleStates);
  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next === current) return;
  window.history.replaceState(window.history.state, "", next);
  window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
}
